import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { AlertCircle, FileText, Download, Loader2, CheckCircle2, Upload, Link, File, X, Edit3, RefreshCw, Plus, Trash2, Library, Clock, ExternalLink, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VIKTILABS_LOGO = "https://customer-assets.emergentagent.com/job_2452956a-6453-4a30-93fe-efe6b3d5e952/artifacts/vf309x22_Logo_Viktilabs_black_Zeichenfla%CC%88che%201.png";

const ICONS = {
  pill: "https://customer-assets.emergentagent.com/job_2452956a-6453-4a30-93fe-efe6b3d5e952/artifacts/2b0g1lv0_VL_Icons_Pill.png",
  glas: "https://customer-assets.emergentagent.com/job_2452956a-6453-4a30-93fe-efe6b3d5e952/artifacts/o41u0blt_VL_Icons_Glas.png",
};

function App() {
  const [activeTab, setActiveTab] = useState("create"); // "create" or "library"
  const [url, setUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [scrapedData, setScrapedData] = useState(null);
  const [generatedSheet, setGeneratedSheet] = useState(null);
  const [editedSheet, setEditedSheet] = useState(null); // For editable texts
  const [additionalData, setAdditionalData] = useState({});
  const [uploadedImage, setUploadedImage] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [currentSheetId, setCurrentSheetId] = useState(null); // For editing existing sheets
  const [companyAddress, setCompanyAddress] = useState("viktilabs · Mahonienweg 22f · 12437 Berlin");
  
  // Library state
  const [librarySheets, setLibrarySheets] = useState([]);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
  
  // Additional document uploads
  const [uploadedDocs, setUploadedDocs] = useState({
    productInfo: null,
    analyses: null,
    studies: null
  });
  const [additionalText, setAdditionalText] = useState("");

  // Load library on mount and when tab changes
  useEffect(() => {
    if (activeTab === "library") {
      loadLibrary();
    }
  }, [activeTab]);

  const loadLibrary = async () => {
    setIsLoadingLibrary(true);
    try {
      const response = await axios.get(`${API}/library`);
      setLibrarySheets(response.data.sheets || []);
    } catch (error) {
      toast.error("Fehler beim Laden der Bibliothek");
    } finally {
      setIsLoadingLibrary(false);
    }
  };

  const loadSheetFromLibrary = async (sheetId) => {
    setIsLoading(true);
    setLoadingMessage("Datenblatt wird geladen...");
    try {
      const response = await axios.get(`${API}/library/${sheetId}`);
      const sheet = response.data;
      
      // Restore all state
      setScrapedData(sheet.scraped_data);
      setGeneratedSheet(sheet.generated_sheet);
      setEditedSheet(JSON.parse(JSON.stringify(sheet.generated_sheet)));
      setUrl(sheet.product_url);
      setCurrentSheetId(sheetId);
      setPdfUrl(sheet.pdf_id ? `${API}/pdf/${sheet.pdf_id}` : null);
      
      // Switch to create tab to show the loaded sheet
      setActiveTab("create");
      toast.success("Datenblatt geladen - Sie können jetzt weiterarbeiten");
    } catch (error) {
      toast.error("Fehler beim Laden des Datenblatts");
    } finally {
      setIsLoading(false);
      setLoadingMessage("");
    }
  };

  const saveToLibrary = async () => {
    if (!scrapedData || !editedSheet) {
      toast.error("Kein Datenblatt zum Speichern vorhanden");
      return;
    }
    
    setIsLoading(true);
    setLoadingMessage("Wird gespeichert...");
    try {
      const payload = {
        scraped_data: scrapedData,
        generated_sheet: editedSheet,
        pdf_id: pdfUrl ? pdfUrl.split('/').pop() : null
      };
      
      let response;
      if (currentSheetId) {
        // Update existing
        response = await axios.put(`${API}/library/${currentSheetId}`, payload);
        toast.success("Datenblatt aktualisiert");
      } else {
        // Save new
        response = await axios.post(`${API}/library/save`, payload);
        setCurrentSheetId(response.data.id);
        toast.success("Datenblatt in Bibliothek gespeichert");
      }
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setIsLoading(false);
      setLoadingMessage("");
    }
  };

  const deleteFromLibrary = async (sheetId, e) => {
    e.stopPropagation();
    if (!window.confirm("Datenblatt wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/library/${sheetId}`);
      setLibrarySheets(prev => prev.filter(s => s.id !== sheetId));
      toast.success("Datenblatt gelöscht");
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const startNewSheet = () => {
    setScrapedData(null);
    setGeneratedSheet(null);
    setEditedSheet(null);
    setUrl("");
    setPdfUrl(null);
    setCurrentSheetId(null);
    setAdditionalData({});
    setUploadedImage(null);
    setActiveTab("create");
  };

  const handleGenerateTexts = async (data) => {
    if (!data) return;
    setIsLoading(true);
    setLoadingMessage("Produktblatt wird mit KI erstellt...");
    try {
      const response = await axios.post(`${API}/generate`, {
        scraped_data: data,
        additional_data: additionalData,
        uploaded_image_base64: uploadedImage,
      });
      setGeneratedSheet(response.data);
      setEditedSheet(JSON.parse(JSON.stringify(response.data))); // Deep copy for editing
      setPdfUrl(null); // Reset PDF URL when regenerating
      setCurrentSheetId(null); // This is a new sheet now
      toast.success("Produktblatt erfolgreich erstellt!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen");
    } finally {
      setIsLoading(false);
      setLoadingMessage("");
    }
  };

  // Apply edits and regenerate preview
  const handleApplyEdits = () => {
    setGeneratedSheet(JSON.parse(JSON.stringify(editedSheet)));
    setPdfUrl(null); // Reset PDF URL to require new generation
    toast.success("Änderungen wurden übernommen!");
  };

  // Reset edits to original
  const handleResetEdits = () => {
    setEditedSheet(JSON.parse(JSON.stringify(generatedSheet)));
    toast.info("Änderungen wurden zurückgesetzt");
  };

  // Helper to update array items
  const updateArrayItem = (field, index, value) => {
    setEditedSheet(prev => {
      const newArr = [...(prev[field] || [])];
      newArr[index] = value;
      return { ...prev, [field]: newArr };
    });
  };

  // Helper to add array item
  const addArrayItem = (field) => {
    setEditedSheet(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), ""]
    }));
  };

  // Helper to remove array item
  const removeArrayItem = (field, index) => {
    setEditedSheet(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index)
    }));
  };

  const handleScrape = async () => {
    if (!url.trim()) {
      toast.error("Bitte gib einen Produktlink ein");
      return;
    }
    if (!url.includes("viktilabs.de")) {
      toast.error("Nur viktilabs.de Links werden unterstützt");
      return;
    }
    setIsLoading(true);
    setLoadingMessage("Produktdaten werden extrahiert...");
    setScrapedData(null);
    setGeneratedSheet(null);
    setAdditionalData({});
    setUploadedImage(null);

    try {
      const response = await axios.post(`${API}/scrape`, { url: url.trim() });
      setScrapedData(response.data);
      if (response.data.is_complete) {
        toast.success("Produktdaten erfolgreich extrahiert!");
        await handleGenerateTexts(response.data);
      } else {
        toast.warning("Einige Daten fehlen. Bitte ergänze die fehlenden Informationen.");
        setIsLoading(false);
        setLoadingMessage("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Extrahieren");
      setIsLoading(false);
      setLoadingMessage("");
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result;
      if (typeof result === 'string') {
        setUploadedImage(result.split(",")[1]);
        toast.success("Bild hochgeladen");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDownloadPDF = async () => {
    if (!scrapedData || !generatedSheet) return;
    
    setIsLoading(true);
    setLoadingMessage("PDF wird erstellt...");
    
    try {
      // Use editedSheet if available, otherwise use generatedSheet
      const sheetToUse = editedSheet || generatedSheet;
      
      const response = await axios.post(`${API}/generate-pdf`, {
        scraped_data: scrapedData,
        generated_sheet: sheetToUse,
        uploaded_image_base64: uploadedImage,
        company_address: companyAddress,
      });
      
      // Set direct URL to PDF
      const directUrl = `${BACKEND_URL}/api/pdf/${response.data.pdf_id}`;
      setPdfUrl(directUrl);
      
      toast.success("PDF bereit! Klicke den grünen Button.");
    } catch (error) {
      console.error(error);
      toast.error("Fehler beim PDF-Erstellen");
    } finally {
      setIsLoading(false);
      setLoadingMessage("");
    }
  };

  const colorScheme = scrapedData?.color_scheme || { dark: "#305A46", light: "#E7F1E8" };

  const renderPhysiological = () => {
    if (!generatedSheet?.page2_physiological) return null;
    return generatedSheet.page2_physiological.map((item, i) => (
      <li key={`phys-${i}`} className="font-montserrat text-sm text-gray-700 flex items-start gap-2">
        <span style={{ color: colorScheme.dark }}>•</span>
        {item}
      </li>
    ));
  };

  const renderApplications = () => {
    if (!generatedSheet?.page2_applications) return null;
    return generatedSheet.page2_applications.map((item, i) => (
      <li key={`app-${i}`} className="font-montserrat text-sm text-white flex items-start gap-2">
        <span>•</span>
        {item}
      </li>
    ));
  };

  const renderQuality = () => {
    if (!generatedSheet?.page2_quality_features) return null;
    return generatedSheet.page2_quality_features.map((item, i) => (
      <li key={`qual-${i}`} className="font-montserrat text-sm flex items-center gap-2" style={{ color: colorScheme.dark }}>
        <CheckCircle2 className="w-4 h-4" />
        {item}
      </li>
    ));
  };

  const renderComposition = () => {
    if (!generatedSheet?.page3_composition_table) return null;
    return generatedSheet.page3_composition_table.map((row, i) => (
      <tr key={`comp-${i}`}>
        <td className="font-montserrat">{row.name}</td>
        <td className="font-montserrat">{row.amount}</td>
      </tr>
    ));
  };

  const renderNotes = () => {
    if (!generatedSheet?.page4_important_notes) return null;
    return generatedSheet.page4_important_notes.map((note, i) => (
      <li key={`note-${i}`} className="font-montserrat text-sm flex items-start gap-2" style={{ color: colorScheme.dark }}>
        <span>•</span>
        {note}
      </li>
    ));
  };

  const renderMissingFields = () => {
    if (!scrapedData?.missing_fields) return null;
    return scrapedData.missing_fields.map((field) => (
      <div key={field.field_name} className="bg-white rounded-lg p-4 border border-amber-200">
        <Label className="font-montserrat font-medium text-gray-700 mb-2 block">
          {field.description}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </Label>
        {field.field_type === "image" ? (
          <div className="space-y-2">
            <label className="cursor-pointer inline-block">
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              <div className="flex items-center gap-2 px-4 py-2 bg-[#E7F1E8] text-[#305A46] rounded-lg hover:bg-[#d9e9db] font-montserrat font-medium">
                <Upload className="w-4 h-4" />
                Bild hochladen
              </div>
            </label>
            {uploadedImage && (
              <img src={`data:image/png;base64,${uploadedImage}`} alt="Uploaded" className="w-32 h-32 object-contain rounded-lg border" />
            )}
          </div>
        ) : field.field_type === "list" ? (
          <Textarea
            placeholder="Inhaltsstoffe eingeben"
            onChange={(e) => setAdditionalData((prev) => ({ ...prev, [field.field_name]: e.target.value }))}
            className="font-montserrat"
          />
        ) : (
          <Input
            type="text"
            placeholder={field.description}
            onChange={(e) => setAdditionalData((prev) => ({ ...prev, [field.field_name]: e.target.value }))}
            className="font-montserrat"
          />
        )}
      </div>
    ));
  };

  return (
    <div className="App min-h-screen bg-[#FAFAFA]">
      <Toaster position="top-right" richColors />

      {isLoading && (
        <div className="loading-overlay" data-testid="loading-overlay">
          <Loader2 className="w-12 h-12 text-[#305A46] animate-spin" />
          <p className="loading-text mt-4">{loadingMessage}</p>
        </div>
      )}

      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <img src={VIKTILABS_LOGO} alt="Viktilabs" className="h-8" data-testid="logo" />
          <div className="flex items-center gap-4">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab("create")}
                className={`px-4 py-2 rounded-md text-sm font-montserrat font-medium transition-colors ${
                  activeTab === "create" 
                    ? "bg-white text-[#305A46] shadow-sm" 
                    : "text-gray-600 hover:text-gray-900"
                }`}
                data-testid="tab-create"
              >
                <Plus className="w-4 h-4 inline mr-2" />
                Neues Datenblatt
              </button>
              <button
                onClick={() => setActiveTab("library")}
                className={`px-4 py-2 rounded-md text-sm font-montserrat font-medium transition-colors ${
                  activeTab === "library" 
                    ? "bg-white text-[#305A46] shadow-sm" 
                    : "text-gray-600 hover:text-gray-900"
                }`}
                data-testid="tab-library"
              >
                <Library className="w-4 h-4 inline mr-2" />
                Bibliothek
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* LIBRARY VIEW */}
        {activeTab === "library" && (
          <div data-testid="library-view">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-montserrat font-bold text-gray-900">Meine Datenblätter</h1>
                <p className="text-gray-600 font-montserrat">Klicke auf ein Datenblatt, um es zu bearbeiten</p>
              </div>
              <Button
                onClick={startNewSheet}
                className="bg-[#305A46] hover:bg-[#264839] font-montserrat"
                data-testid="new-sheet-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Neues Datenblatt
              </Button>
            </div>

            {isLoadingLibrary ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-[#305A46] animate-spin" />
              </div>
            ) : librarySheets.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Library className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 font-montserrat">Noch keine Datenblätter gespeichert</p>
                  <Button
                    onClick={startNewSheet}
                    variant="outline"
                    className="mt-4 font-montserrat"
                  >
                    Erstes Datenblatt erstellen
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {librarySheets.map((sheet) => (
                  <Card
                    key={sheet.id}
                    className="cursor-pointer hover:shadow-lg transition-shadow border-gray-200 hover:border-[#305A46]"
                    onClick={() => loadSheetFromLibrary(sheet.id)}
                    data-testid={`library-card-${sheet.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {sheet.thumbnail_url ? (
                          <img
                            src={sheet.thumbnail_url}
                            alt={sheet.product_name}
                            className="w-16 h-16 object-contain rounded bg-gray-50"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center">
                            <FileText className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-montserrat font-semibold text-gray-900 truncate">
                            {sheet.product_name}
                          </h3>
                          <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                            <Clock className="w-3 h-3" />
                            {new Date(sheet.updated_at).toLocaleDateString('de-DE', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <a
                              href={sheet.product_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Produktseite
                            </a>
                            <button
                              onClick={(e) => deleteFromLibrary(sheet.id, e)}
                              className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 ml-auto"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CREATE/EDIT VIEW */}
        {activeTab === "create" && (
          <div data-testid="create-view">
            {/* Show "editing existing" banner if loading from library */}
            {currentSheetId && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-2 text-blue-800">
                  <Edit3 className="w-4 h-4" />
                  <span className="font-montserrat text-sm">
                    Bearbeitung: <strong>{scrapedData?.product_name}</strong>
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={startNewSheet}
                  className="text-blue-700 border-blue-300 hover:bg-blue-100"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Neues Datenblatt
                </Button>
              </div>
            )}

        <Card className="mb-8" data-testid="url-input-card">
          <CardHeader>
            <CardTitle className="font-montserrat text-xl text-gray-900">Produktblatt erstellen</CardTitle>
            <CardDescription className="font-montserrat">
              Gib einen viktilabs.de Produktlink ein, um automatisch ein Produktblatt zu generieren.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="url"
                  placeholder="https://www.viktilabs.de/products/..."
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="pl-10 h-12 font-montserrat"
                  data-testid="url-input"
                />
              </div>
              <Button
                onClick={handleScrape}
                disabled={isLoading || !url.trim()}
                className="h-12 px-8 bg-[#305A46] hover:bg-[#264839] font-montserrat font-semibold"
                data-testid="generate-btn"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><FileText className="w-5 h-5 mr-2" />Produktblatt erstellen</>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Additional Documents Upload Section */}
        <Card className="mb-8 border-blue-200 bg-blue-50/50" data-testid="additional-docs-card">
          <CardHeader>
            <CardTitle className="font-montserrat text-lg text-blue-800 flex items-center gap-2">
              <File className="w-5 h-5" />
              Zusätzliche Dokumente (optional)
            </CardTitle>
            <CardDescription className="text-blue-700">
              Lade zusätzliche Produktinformationen, Analysen oder Studien hoch, um die KI-Textqualität zu verbessern.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* Product Info Upload */}
              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <Label className="font-montserrat font-medium text-gray-700 mb-2 block text-sm">
                  Produktinformationen
                </Label>
                <label className="cursor-pointer block">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setUploadedDocs(prev => ({ ...prev, productInfo: file.name }));
                        toast.success(`${file.name} hochgeladen`);
                      }
                    }}
                    className="hidden"
                  />
                  <div className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors font-montserrat text-sm">
                    <Upload className="w-4 h-4" />
                    {uploadedDocs.productInfo || "Datei wählen"}
                  </div>
                </label>
              </div>

              {/* Studies Upload */}
              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <Label className="font-montserrat font-medium text-gray-700 mb-2 block text-sm">
                  Studien / Quellen
                </Label>
                <label className="cursor-pointer block">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setUploadedDocs(prev => ({ ...prev, studies: file.name }));
                        toast.success(`${file.name} hochgeladen`);
                      }
                    }}
                    className="hidden"
                  />
                  <div className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors font-montserrat text-sm">
                    <Upload className="w-4 h-4" />
                    {uploadedDocs.studies || "Datei wählen"}
                  </div>
                </label>
              </div>
            </div>

            {/* Additional Text Input */}
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <Label className="font-montserrat font-medium text-gray-700 mb-2 block text-sm">
                Zusätzliche Hinweise für die KI
              </Label>
              <Textarea
                placeholder="z.B. spezifische Health Claims, besondere Produkteigenschaften, Zielgruppen-Informationen..."
                value={additionalText}
                onChange={(e) => setAdditionalText(e.target.value)}
                className="font-montserrat text-sm min-h-[80px]"
              />
            </div>
          </CardContent>
        </Card>

        {scrapedData && !scrapedData.is_complete && scrapedData.missing_fields?.length > 0 && (
          <Card className="mb-8 border-amber-300 bg-amber-50" data-testid="missing-data-card">
            <CardHeader>
              <CardTitle className="font-montserrat text-lg text-amber-800 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Fehlende Daten ergänzen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {renderMissingFields()}
              <Button
                onClick={() => handleGenerateTexts(scrapedData)}
                disabled={isLoading}
                className="w-full h-12 bg-[#305A46] hover:bg-[#264839] font-montserrat font-semibold mt-4"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Mit ergänzten Daten generieren"}
              </Button>
            </CardContent>
          </Card>
        )}

        {scrapedData && (
          <Card className="mb-8" data-testid="scraped-data-card">
            <CardHeader>
              <CardTitle className="font-montserrat text-lg flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                Extrahierte Produktdaten
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 font-montserrat mb-1">Produktname</p>
                  <p className="font-montserrat font-medium text-sm">{scrapedData.product_name || "-"}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 font-montserrat mb-1">Tagesdosis</p>
                  <p className="font-montserrat font-medium text-sm">{scrapedData.daily_dose || "-"}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 font-montserrat mb-1">Packungsgröße</p>
                  <p className="font-montserrat font-medium text-sm">{scrapedData.package_size || "-"}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 font-montserrat mb-1">Inhaltsstoffe</p>
                  <p className="font-montserrat font-medium text-sm">{scrapedData.ingredients?.length || 0} gefunden</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {generatedSheet && scrapedData && (
          <div className="space-y-8" data-testid="preview-section">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <h2 className="font-montserrat font-semibold text-xl text-gray-900">Produktblatt Vorschau</h2>
              <div className="flex items-center gap-3">
                <Button 
                  onClick={saveToLibrary} 
                  disabled={isLoading} 
                  variant="outline"
                  className="font-montserrat font-semibold border-[#305A46] text-[#305A46] hover:bg-[#305A46]/10" 
                  data-testid="save-library-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {currentSheetId ? "Aktualisieren" : "In Bibliothek speichern"}
                </Button>
                {!pdfUrl ? (
                  <Button onClick={handleDownloadPDF} disabled={isLoading} className="bg-[#305A46] hover:bg-[#264839] font-montserrat font-semibold" data-testid="download-btn">
                    <Download className="w-4 h-4 mr-2" />
                    PDF erstellen
                  </Button>
                ) : (
                  <a
                    href={pdfUrl}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-montserrat font-semibold text-base transition-colors"
                    data-testid="download-link"
                  >
                    <Download className="w-5 h-5" />
                    PDF herunterladen
                  </a>
                )}
              </div>
            </div>

            <div className="pdf-container">
              <div className="pdf-page" style={{ borderTop: `3px solid ${colorScheme.dark}` }} data-testid="pdf-page-1">
                <div className="pdf-page-header" style={{ borderBottom: `3px solid ${colorScheme.dark}` }}>
                  <span>01 · Produkt-Datenblatt · {scrapedData.product_name}</span>
                  <img src={VIKTILABS_LOGO} alt="Viktilabs" className="h-5" />
                </div>
                <div className="pdf-page-content">
                  <div className="flex gap-6 mb-6">
                    {(uploadedImage || scrapedData.product_image_url) && (
                      <img src={uploadedImage ? `data:image/png;base64,${uploadedImage}` : scrapedData.product_image_url} alt={scrapedData.product_name} className="w-40 h-auto object-contain" />
                    )}
                    <div className="flex-1">
                      <h1 className="font-montserrat font-bold text-xl mb-4" style={{ color: colorScheme.dark }}>{scrapedData.product_name}</h1>
                      <p className="font-montserrat text-sm text-gray-700 leading-relaxed mb-4">{generatedSheet.page1_intro}</p>
                    </div>
                  </div>
                  <div className="mb-6">
                    <h2 className="font-montserrat font-semibold text-base mb-3" style={{ color: colorScheme.dark }}>Hintergrund</h2>
                    <p className="font-montserrat text-sm text-gray-700 leading-relaxed">{generatedSheet.page1_background}</p>
                  </div>
                </div>
                <div className="pdf-page-footer"><span className="font-opensans">viktilabs. Mahonienweg 22f 12437 Berlin</span></div>
              </div>

              <div className="pdf-page" style={{ borderTop: `3px solid ${colorScheme.dark}` }} data-testid="pdf-page-2">
                <div className="pdf-page-header" style={{ borderBottom: `3px solid ${colorScheme.dark}` }}>
                  <span>02 · Produkt-Datenblatt · {scrapedData.product_name}</span>
                  <img src={VIKTILABS_LOGO} alt="Viktilabs" className="h-5" />
                </div>
                <div className="pdf-page-content">
                  <div className="mb-6">
                    <h2 className="font-montserrat font-semibold text-base mb-3" style={{ color: colorScheme.dark }}>Physiologische Bedeutung der Inhaltsstoffe:</h2>
                    <ul className="space-y-2">{renderPhysiological()}</ul>
                  </div>
                  <div className="rounded-lg p-4 mb-6" style={{ backgroundColor: colorScheme.dark }}>
                    <h2 className="font-montserrat font-semibold text-base mb-3 text-white">Mögliche Anwendungsbereiche:</h2>
                    <ul className="space-y-2">{renderApplications()}</ul>
                  </div>
                  <div className="rounded-lg p-4" style={{ backgroundColor: colorScheme.light }}>
                    <h2 className="font-montserrat font-semibold text-base mb-3" style={{ color: colorScheme.dark }}>Qualitätsmerkmale:</h2>
                    <ul className="grid grid-cols-2 gap-2">{renderQuality()}</ul>
                  </div>
                </div>
                <div className="pdf-page-footer"><span className="font-opensans">viktilabs. Mahonienweg 22f 12437 Berlin</span></div>
              </div>

              <div className="pdf-page" style={{ borderTop: `3px solid ${colorScheme.dark}` }} data-testid="pdf-page-3">
                <div className="pdf-page-header" style={{ borderBottom: `3px solid ${colorScheme.dark}` }}>
                  <span>03 · Produkt-Datenblatt · {scrapedData.product_name}</span>
                  <img src={VIKTILABS_LOGO} alt="Viktilabs" className="h-5" />
                </div>
                <div className="pdf-page-content">
                  <h2 className="font-montserrat font-semibold text-base mb-4" style={{ color: colorScheme.dark }}>Zusammensetzung pro {scrapedData.daily_dose || "Tagesdosis"}*</h2>
                  <table className="ingredients-table mb-6">
                    <thead><tr><th>Inhaltsstoffe pro Tagesdosis</th><th>{scrapedData.daily_dose}</th></tr></thead>
                    <tbody>{renderComposition()}</tbody>
                  </table>
                  <p className="font-montserrat text-xs text-gray-500 mb-6">* Nährstoff-Referenzwerte (NRV) gemäß EU-Verordnung 1169/2011<br />*** Keine Nährstoff-Referenzwerte (NRV) vorhanden</p>
                  <div>
                    <h3 className="font-montserrat font-semibold text-sm mb-2" style={{ color: colorScheme.dark }}>Zutaten:</h3>
                    <p className="font-montserrat text-sm text-gray-700">{generatedSheet.page3_ingredients || scrapedData.raw_ingredients}</p>
                  </div>
                </div>
                <div className="pdf-page-footer"><span className="font-opensans">viktilabs. Mahonienweg 22f 12437 Berlin</span></div>
              </div>

              <div className="pdf-page" style={{ borderTop: `3px solid ${colorScheme.dark}` }} data-testid="pdf-page-4">
                <div className="pdf-page-header" style={{ borderBottom: `3px solid ${colorScheme.dark}` }}>
                  <span>04 · Produkt-Datenblatt · {scrapedData.product_name}</span>
                  <img src={VIKTILABS_LOGO} alt="Viktilabs" className="h-5" />
                </div>
                <div className="pdf-page-content">
                  <div className="flex items-start gap-4 mb-6">
                    <img src={ICONS.pill} alt="Verzehr" className="w-10 h-10" />
                    <div>
                      <h2 className="font-montserrat font-semibold text-base mb-2" style={{ color: colorScheme.dark }}>Verzehrempfehlung</h2>
                      <p className="font-montserrat text-sm text-gray-700">{generatedSheet.page4_consumption}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 mb-6">
                    <img src={ICONS.glas} alt="Packung" className="w-10 h-10" />
                    <div>
                      <h2 className="font-montserrat font-semibold text-base mb-2" style={{ color: colorScheme.dark }}>Packungsgröße</h2>
                      <p className="font-montserrat text-sm text-gray-700">{generatedSheet.page4_package_size}</p>
                    </div>
                  </div>
                  <div className="rounded-lg p-4" style={{ backgroundColor: colorScheme.light }}>
                    <h2 className="font-montserrat font-semibold text-base mb-3" style={{ color: colorScheme.dark }}>Wichtige Hinweise</h2>
                    <ul className="space-y-2">{renderNotes()}</ul>
                  </div>
                  <p className="font-montserrat text-xs text-gray-500 mt-6">Alle Angaben ohne Gewähr. Irrtümer, Auslassungen und Änderungen vorbehalten.</p>
                </div>
                <div className="pdf-page-footer"><span className="font-opensans">viktilabs. Mahonienweg 22f 12437 Berlin</span></div>
              </div>
            </div>

            {/* ========== TEXT EDITING SECTION ========== */}
            {editedSheet && (
              <Card className="mt-8 border-amber-300 bg-amber-50/50" data-testid="text-editor-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-montserrat text-lg text-amber-800 flex items-center gap-2">
                        <Edit3 className="w-5 h-5" />
                        Texte bearbeiten
                      </CardTitle>
                      <CardDescription className="text-amber-700">
                        Hier kannst du die KI-generierten Texte anpassen. Nach der Bearbeitung klicke auf "Änderungen übernehmen".
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={handleResetEdits}
                        className="font-montserrat border-amber-400 text-amber-700 hover:bg-amber-100"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Zurücksetzen
                      </Button>
                      <Button
                        onClick={handleApplyEdits}
                        className="bg-amber-600 hover:bg-amber-700 font-montserrat font-semibold"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Änderungen übernehmen
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  
                  {/* Page 1: Intro & Background */}
                  <div className="bg-white rounded-lg p-4 border border-amber-200">
                    <h3 className="font-montserrat font-semibold text-sm mb-3 text-gray-800">Seite 1: Produktbeschreibung</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <Label className="font-montserrat text-sm text-gray-600 mb-1 block">Produktversprechen (Intro)</Label>
                        <Textarea
                          value={editedSheet.page1_intro || ""}
                          onChange={(e) => setEditedSheet(prev => ({ ...prev, page1_intro: e.target.value }))}
                          className="font-montserrat text-sm min-h-[80px]"
                          placeholder="Produktversprechen eingeben..."
                        />
                      </div>
                      
                      <div>
                        <Label className="font-montserrat text-sm text-gray-600 mb-1 block">Hintergrund</Label>
                        <Textarea
                          value={editedSheet.page1_background || ""}
                          onChange={(e) => setEditedSheet(prev => ({ ...prev, page1_background: e.target.value }))}
                          className="font-montserrat text-sm min-h-[100px]"
                          placeholder="Hintergrund-Text eingeben..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Page 2: Physiological, Applications, Quality */}
                  <div className="bg-white rounded-lg p-4 border border-amber-200">
                    <h3 className="font-montserrat font-semibold text-sm mb-3 text-gray-800">Seite 2: Wirklogik & Qualität</h3>
                    
                    <div className="space-y-4">
                      {/* Physiological */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label className="font-montserrat text-sm text-gray-600">Physiologische Bedeutung</Label>
                          <Button variant="ghost" size="sm" onClick={() => addArrayItem('page2_physiological')} className="text-amber-600 hover:text-amber-700">
                            <Plus className="w-4 h-4 mr-1" /> Hinzufügen
                          </Button>
                        </div>
                        {(editedSheet.page2_physiological || []).map((item, i) => (
                          <div key={`phys-${i}`} className="flex gap-2 mb-2">
                            <Input
                              value={item}
                              onChange={(e) => updateArrayItem('page2_physiological', i, e.target.value)}
                              className="font-montserrat text-sm flex-1"
                            />
                            <Button variant="ghost" size="sm" onClick={() => removeArrayItem('page2_physiological', i)} className="text-red-500 hover:text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>

                      {/* Applications */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label className="font-montserrat text-sm text-gray-600">Anwendungsbereiche</Label>
                          <Button variant="ghost" size="sm" onClick={() => addArrayItem('page2_applications')} className="text-amber-600 hover:text-amber-700">
                            <Plus className="w-4 h-4 mr-1" /> Hinzufügen
                          </Button>
                        </div>
                        {(editedSheet.page2_applications || []).map((item, i) => (
                          <div key={`app-${i}`} className="flex gap-2 mb-2">
                            <Input
                              value={item}
                              onChange={(e) => updateArrayItem('page2_applications', i, e.target.value)}
                              className="font-montserrat text-sm flex-1"
                            />
                            <Button variant="ghost" size="sm" onClick={() => removeArrayItem('page2_applications', i)} className="text-red-500 hover:text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>

                      {/* Quality Features */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label className="font-montserrat text-sm text-gray-600">Qualitätsmerkmale</Label>
                          <Button variant="ghost" size="sm" onClick={() => addArrayItem('page2_quality_features')} className="text-amber-600 hover:text-amber-700">
                            <Plus className="w-4 h-4 mr-1" /> Hinzufügen
                          </Button>
                        </div>
                        {(editedSheet.page2_quality_features || []).map((item, i) => (
                          <div key={`qual-${i}`} className="flex gap-2 mb-2">
                            <Input
                              value={item}
                              onChange={(e) => updateArrayItem('page2_quality_features', i, e.target.value)}
                              className="font-montserrat text-sm flex-1"
                            />
                            <Button variant="ghost" size="sm" onClick={() => removeArrayItem('page2_quality_features', i)} className="text-red-500 hover:text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Page 3: Ingredients */}
                  <div className="bg-white rounded-lg p-4 border border-amber-200">
                    <h3 className="font-montserrat font-semibold text-sm mb-3 text-gray-800">Seite 3: Zutaten</h3>
                    
                    <div>
                      <Label className="font-montserrat text-sm text-gray-600 mb-1 block">Zutaten-Text</Label>
                      <Textarea
                        value={editedSheet.page3_ingredients || ""}
                        onChange={(e) => setEditedSheet(prev => ({ ...prev, page3_ingredients: e.target.value }))}
                        className="font-montserrat text-sm min-h-[80px]"
                        placeholder="Zutaten-Text eingeben..."
                      />
                    </div>
                  </div>

                  {/* Page 4: Consumption, Package, Notes */}
                  <div className="bg-white rounded-lg p-4 border border-amber-200">
                    <h3 className="font-montserrat font-semibold text-sm mb-3 text-gray-800">Seite 4: Verzehr & Hinweise</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <Label className="font-montserrat text-sm text-gray-600 mb-1 block">Verzehrempfehlung</Label>
                        <Textarea
                          value={editedSheet.page4_consumption || ""}
                          onChange={(e) => setEditedSheet(prev => ({ ...prev, page4_consumption: e.target.value }))}
                          className="font-montserrat text-sm min-h-[60px]"
                          placeholder="Verzehrempfehlung eingeben..."
                        />
                      </div>

                      <div>
                        <Label className="font-montserrat text-sm text-gray-600 mb-1 block">Packungsgröße</Label>
                        <Textarea
                          value={editedSheet.page4_package_size || ""}
                          onChange={(e) => setEditedSheet(prev => ({ ...prev, page4_package_size: e.target.value }))}
                          className="font-montserrat text-sm min-h-[40px]"
                          placeholder="Packungsgröße eingeben..."
                        />
                      </div>

                      {/* Notes */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label className="font-montserrat text-sm text-gray-600">Wichtige Hinweise</Label>
                          <Button variant="ghost" size="sm" onClick={() => addArrayItem('page4_important_notes')} className="text-amber-600 hover:text-amber-700">
                            <Plus className="w-4 h-4 mr-1" /> Hinzufügen
                          </Button>
                        </div>
                        {(editedSheet.page4_important_notes || []).map((item, i) => (
                          <div key={`note-${i}`} className="flex gap-2 mb-2">
                            <Textarea
                              value={item}
                              onChange={(e) => updateArrayItem('page4_important_notes', i, e.target.value)}
                              className="font-montserrat text-sm flex-1 min-h-[40px]"
                            />
                            <Button variant="ghost" size="sm" onClick={() => removeArrayItem('page4_important_notes', i)} className="text-red-500 hover:text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Company Address */}
                  <div className="bg-white rounded-lg p-4 border border-amber-200">
                    <h3 className="font-montserrat font-semibold text-sm mb-3 text-gray-800">Firmenadresse (Footer)</h3>
                    <div>
                      <Label className="font-montserrat text-sm text-gray-600 mb-1 block">Adresse für den PDF-Footer</Label>
                      <Input
                        value={companyAddress}
                        onChange={(e) => setCompanyAddress(e.target.value)}
                        className="font-montserrat text-sm"
                        placeholder="z.B. viktilabs · Mahonienweg 22f · 12437 Berlin"
                        data-testid="company-address-input"
                      />
                    </div>
                  </div>

                  {/* Apply Button at bottom */}
                  <div className="flex justify-end pt-4">
                    <Button
                      onClick={handleApplyEdits}
                      size="lg"
                      className="bg-amber-600 hover:bg-amber-700 font-montserrat font-semibold"
                    >
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      Änderungen übernehmen & Vorschau aktualisieren
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
        </div>
        )}
      </main>
    </div>
  );
}

export default App;
